import {isRouteErrorResponse, useRouteError} from '@github-ui/react-router'
import {isResponseError} from '@github-ui/react-core/future/response-error'
import {useReportRouteError} from '@github-ui/react-core/future/use-report-route-error'
import {Blankslate} from '@primer/react/experimental'

export interface DashboardRouteErrorBoundaryProps {
  // App name reported to failbot so errors are attributed to the consuming bundle.
  reactAppName?: string
  // Resource noun used in the load-error message, e.g. "pull requests" or "issues".
  resourceName?: string
  // Boundary name reported to failbot as `reactErrorBoundaryName` so critical route/loader
  // failures are attributable to a specific surface (e.g. "IssuesDashboardRoute") instead of
  // landing in the anonymous `N/A` bucket shared by every unnamed critical error.
  boundaryName?: string
}

export function DashboardRouteErrorBoundary({
  reactAppName = 'dashboard-surface',
  resourceName = 'pull requests',
  boundaryName,
}: DashboardRouteErrorBoundaryProps = {}) {
  const routeError = useRouteError()
  useReportRouteError(routeError, {reactAppName, boundaryName, devLogLabel: 'DashboardRouteErrorBoundary'})

  // 404 during unmount is a benign Turbo navigation race — render nothing
  if (isRouteErrorResponse(routeError) && routeError.status === 404) {
    return null
  }

  if (isResponseError(routeError)) {
    return (
      <Blankslate border={false} spacious={false}>
        <Blankslate.Heading>Unable to load {resourceName}.</Blankslate.Heading>
        <Blankslate.Description>
          The server returned an error. Please reload the page and try again.
        </Blankslate.Description>
      </Blankslate>
    )
  }

  return (
    <Blankslate border={false} spacious={false}>
      <Blankslate.Heading>Something went wrong.</Blankslate.Heading>
      <Blankslate.Description>
        An unexpected error occurred. Please reload the page and try again.
      </Blankslate.Description>
    </Blankslate>
  )
}

// React Router route `ErrorBoundary` slots take a component, not props. Use this to bind
// app-specific options (failbot app name, resource noun) for a route's error boundary.
export function createDashboardRouteErrorBoundary(props: DashboardRouteErrorBoundaryProps) {
  return function BoundDashboardRouteErrorBoundary() {
    return <DashboardRouteErrorBoundary {...props} />
  }
}
